import axiosInstance from "@/lib/axiosInstance";
import axios from "axios";
import type {
  PortalInboxParams,
  PortalInboxResponse,
  PortalMessageChangeResponse,
  PortalMessageHistoryResponse,
  PortalMessagesUnreadCountResponse,
  PortalMessagesReadResponse,
  PortalThreadParams,
  PortalThreadResponse,
  SendPortalMessagePayload,
  SendPortalMessageResponse,
} from "@/types/portalMessages";

export class PortalMessageApiError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message);
    this.name = "PortalMessageApiError";
  }
}

function getPortalMessageApiError(error: unknown, fallback: string) {
  if (axios.isAxiosError(error)) {
    return new PortalMessageApiError(
      error.response?.data?.error || error.response?.data?.message || fallback,
      error.response?.status,
    );
  }

  return new PortalMessageApiError(fallback);
}

export const portalMemberMessageService = {
  async fetchInbox(params: PortalInboxParams = {}) {
    try {
      const response = await axiosInstance.get<PortalInboxResponse>(
        "/member/messages",
        { params },
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message ||
            error.response?.data?.error ||
            "Failed to fetch member conversations",
        );
      }
      throw new Error(
        "An unexpected error occurred during member conversation fetch",
      );
    }
  },

  async fetchUnreadCount() {
    try {
      const response = await axiosInstance.get<PortalMessagesUnreadCountResponse>(
        "/member/messages/unread-count",
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message ||
            error.response?.data?.error ||
            "Failed to fetch unread member message count",
        );
      }
      throw new Error(
        "An unexpected error occurred during unread member message count fetch",
      );
    }
  },

  async fetchThread(memberId: string, params: PortalThreadParams = {}) {
    try {
      const response = await axiosInstance.get<PortalThreadResponse>(
        `/member/${memberId}/messages`,
        {
          params: {
            ...params,
            mode: "canonical",
          },
        },
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message ||
            error.response?.data?.error ||
            "Failed to fetch member thread",
        );
      }
      throw new Error(
        "An unexpected error occurred during member thread fetch",
      );
    }
  },

  async sendMessage(memberId: string, payload: SendPortalMessagePayload) {
    try {
      const response = await axiosInstance.post<SendPortalMessageResponse>(
        `/member/${memberId}/messages`,
        payload,
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message ||
            error.response?.data?.error ||
            "Failed to send member message",
        );
      }
      throw new Error(
        "An unexpected error occurred during member message send",
      );
    }
  },

  async editMessage(memberId: string, messageId: string, text: string) {
    try {
      const response = await axiosInstance.patch<PortalMessageChangeResponse>(
        `/member/${encodeURIComponent(memberId)}/messages/${encodeURIComponent(messageId)}`,
        { text },
      );
      return response.data;
    } catch (error) {
      throw getPortalMessageApiError(error, "Failed to edit member message");
    }
  },

  async removeMessage(memberId: string, messageId: string) {
    try {
      const response = await axiosInstance.delete<PortalMessageChangeResponse>(
        `/member/${encodeURIComponent(memberId)}/messages/${encodeURIComponent(messageId)}`,
      );
      return response.data;
    } catch (error) {
      throw getPortalMessageApiError(error, "Failed to remove member message");
    }
  },

  async fetchMessageHistory(messageId: string) {
    try {
      const response = await axiosInstance.get<PortalMessageHistoryResponse>(
        `/member/messages/${encodeURIComponent(messageId)}/history`,
      );
      return response.data;
    } catch (error) {
      throw getPortalMessageApiError(error, "Failed to load message history");
    }
  },

  async markThreadAsRead(memberId: string, conversationId?: string | null) {
    try {
      const response = await axiosInstance.post<PortalMessagesReadResponse>(
        `/member/${memberId}/messages/read`,
        conversationId ? { conversationId } : undefined,
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message ||
            error.response?.data?.error ||
            "Failed to mark thread as read",
        );
      }
      throw new Error(
        "An unexpected error occurred during thread read update",
      );
    }
  },
};
